import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Ajustes } from '../types'
import {
  AJUSTES_POR_DEFECTO,
  FuncionPendienteError,
  type Adapter,
  type ResultadoStock,
  type Snapshot,
  type Tabla,
} from './adapter'

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined
const KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

export const supabaseConfigurado = Boolean(URL && KEY && URL.startsWith('http'))

// NovaBerry no usa Supabase Auth: el acceso a los datos lo da la clave
// "anon" sola, sin sesión de ningún tipo (ver supabase/schema.sql). Se
// desactiva la persistencia de sesión porque nunca hay una que guardar.
export const supabase: SupabaseClient | null = supabaseConfigurado
  ? createClient(URL!, KEY!, { auth: { persistSession: false, autoRefreshToken: false } })
  : null

/** Las columnas en Postgres se llaman igual que los campos de TypeScript
 *  (entre comillas dobles en el SQL). Así no hace falta una capa de
 *  traducción snake_case ↔ camelCase que solo agregaría lugares donde
 *  equivocarse. */
const TABLAS: Tabla[] = [
  'usuarios',
  'productos',
  'lotes',
  'movimientos',
  'clientes',
  'ventas',
  'devoluciones',
  'bitacora',
  'gastos',
  'asistencia',
]

/** Las dos columnas de `productos` que esta app nunca escribe con un
 *  valor absoluto, porque no es la única que las mueve: el POS del
 *  local descuenta stock sobre las mismas filas.
 *
 *  Mandar la fila entera en el upsert significaba mandar el stock tal
 *  como estaba cuando la app cargó los datos. Editar el precio de un
 *  producto a las 11:00, con una copia de las 9:00, devolvía el stock a
 *  como estaba a las 9:00 y borraba sin ningún error visible todo lo
 *  que el local había vendido en el medio.
 *
 *  PostgREST solo actualiza las columnas que vienen en el payload, así
 *  que omitirlas deja intacto lo que haya en la base. El stock cambia
 *  únicamente por `aplicarStock`. */
const COLUMNAS_DE_STOCK = ['stock', 'reservado']

const sinColumnasDeStock = (fila: Record<string, unknown>) => {
  const copia = { ...fila }
  for (const columna of COLUMNAS_DE_STOCK) delete copia[columna]
  return copia
}

/** Supabase no tira `Error` de JS: tira un objeto plano
 *  (`PostgrestError`, con `.message`, `.code`, etc.). Un `throw error`
 *  directo llega intacto hasta la pantalla, donde todo el código de
 *  guardado hace `err instanceof Error ? err.message : String(err)` —
 *  y `String()` sobre un objeto plano da literalmente `"[object
 *  Object]"`. El vendedor se queda sin saber qué pasó, aunque Postgres
 *  haya mandado un mensaje clarísimo (por ejemplo, el de stock
 *  insuficiente en `nb_aplicar_stock`, ver schema.sql). Todo error de
 *  Supabase pasa por acá antes de salir del adaptador, así el resto de
 *  la app puede confiar en que siempre está viendo un `Error` de
 *  verdad. */
function comoError(error: unknown): Error {
  if (error instanceof Error) return error
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return new Error(error.message)
  }
  return new Error(String(error))
}

/** Trae una tabla completa paginando: Supabase corta en 1000 filas. */
async function traerTodo(sb: SupabaseClient, tabla: string) {
  const filas: Record<string, unknown>[] = []
  const paso = 1000
  for (let desde = 0; ; desde += paso) {
    const { data, error } = await sb.from(tabla).select('*').range(desde, desde + paso - 1)
    if (error) throw comoError(error)
    filas.push(...(data ?? []))
    if (!data || data.length < paso) break
  }
  return filas
}

export const supabaseAdapter: Adapter = {
  nombre: 'supabase',

  async disponible() {
    if (!supabase) return false
    const { error } = await supabase.from('ajustes').select('id').limit(1)
    return !error
  },

  async cargar(): Promise<Snapshot> {
    if (!supabase) throw new Error('Supabase no está configurado.')
    const resultados = await Promise.all(TABLAS.map((t) => traerTodo(supabase, t)))
    const { data: ajustes } = await supabase.from('ajustes').select('*').eq('id', 'ajustes').maybeSingle()

    const snapshot = Object.fromEntries(
      TABLAS.map((t, i) => [t, resultados[i]]),
    ) as unknown as Snapshot
    // Combinado con los valores por defecto, no solo reemplazado: si se
    // agrega un ajuste nuevo (como categoriasGasto) y la fila en la
    // nube todavía no pasó por la migración, no debe llegar `null` y
    // romper la pantalla — hereda el default hasta que se guarde.
    snapshot.ajustes = { ...AJUSTES_POR_DEFECTO, ...(ajustes as Ajustes | null) }
    return snapshot
  },

  async guardar(tabla, filas) {
    if (!supabase || !filas.length) return
    // Un producto nuevo entra con stock 0 (el default de la columna) y
    // el stock inicial se lo carga después un movimiento, para que
    // quede rastro de dónde salió.
    const payload =
      tabla === 'productos'
        ? (filas as unknown as Record<string, unknown>[]).map(sinColumnasDeStock)
        : filas
    const { error } = await supabase.from(tabla).upsert(payload as never[], { onConflict: 'id' })
    if (error) throw comoError(error)
  },

  async aplicarStock(cambios) {
    if (!supabase) throw new Error('Supabase no está configurado.')
    const vacio: ResultadoStock = { stock: {}, movimientos: [] }
    if (!cambios.length) return vacio
    // La suma y la resta las hace Postgres sobre la fila actual (ver
    // nb_aplicar_stock en supabase/schema.sql), igual que hace el POS
    // en pos_registrar_venta. Es la única forma de que dos dispositivos
    // vendiendo el mismo producto no se pisen.
    const { data, error } = await supabase.rpc('nb_aplicar_stock', { p_cambios: cambios })
    if (error) {
      // PGRST202 = PostgREST no encuentra la función. Pasa cuando la
      // base todavía no pasó por la última versión de schema.sql. Sin
      // este mensaje, el vendedor solo vería "Could not find the
      // function public.nb_aplicar_stock in the schema cache" y no
      // tendría idea de qué hacer con eso.
      if (error.code === 'PGRST202') {
        throw new Error(
          'Falta actualizar la base de datos: corre supabase/schema.sql completo en el SQL Editor de Supabase (no existe la función nb_aplicar_stock).',
        )
      }
      throw comoError(error)
    }
    const resultado = data as Partial<ResultadoStock> | null
    return { stock: resultado?.stock ?? {}, movimientos: resultado?.movimientos ?? [] }
  },

  async registrarVenta(venta, movimientos) {
    if (!supabase) throw new Error('Supabase no está configurado.')
    // Todo en una sola llamada a Postgres (ver nb_registrar_venta en
    // supabase/schema.sql): stock, venta y lotes quedan juntos o no queda
    // nada. Los lotes se descuentan allá a partir de `venta.lineas`, sobre
    // el valor que tiene la base en ese momento —no se manda la copia de
    // memoria, que puede estar atrasada respecto del POS—.
    const { data, error } = await supabase.rpc('nb_registrar_venta', {
      p_venta: venta,
      p_movimientos: movimientos,
    })
    if (error) {
      if (error.code === 'PGRST202') throw new FuncionPendienteError('nb_registrar_venta')
      throw comoError(error)
    }
    const r = data as Partial<ResultadoStock & { folio: string; repetida: boolean }> | null
    return {
      stock: r?.stock ?? {},
      movimientos: r?.movimientos ?? [],
      folio: r?.folio ?? venta.folio,
      repetida: r?.repetida ?? false,
    }
  },

  async eliminar(tabla: Tabla, ids: string[]) {
    if (!supabase || !ids.length) return
    const { error } = await supabase.from(tabla).delete().in('id', ids)
    if (error) throw comoError(error)
  },

  async guardarAjustes(ajustes: Ajustes) {
    if (!supabase) return
    const { error } = await supabase.from('ajustes').upsert(ajustes, { onConflict: 'id' })
    if (error) throw comoError(error)
  },

  async reemplazarTodo(s: Snapshot) {
    if (!supabase) return
    // CUIDADO: esto vacía las tablas compartidas con el POS del local
    // (`productos`, `movimientos`, `clientes`, `usuarios`). Todo lo que
    // el POS haya registrado y no venga en el respaldo —sus ventas del
    // día, sus movimientos de stock— se pierde sin manera de
    // recuperarlo. La app solo debe llegar hasta acá después de una
    // confirmación explícita que lo diga con esas palabras (ver
    // Ajustes → Respaldo completo).
    //
    // Se borra en orden inverso a las dependencias antes de reinsertar.
    // Cada operación revisa su error: sin esto, un rechazo de RLS (por
    // ejemplo, escribir sin sesión) pasaba desapercibido y la app creía
    // haber guardado datos que en realidad nunca llegaron a la base.
    for (const tabla of [...TABLAS].reverse()) {
      const { error } = await supabase.from(tabla).delete().neq('id', '')
      if (error) throw comoError(error)
    }
    for (const tabla of TABLAS) {
      const filas = s[tabla] as unknown[]
      if (filas.length) {
        const { error } = await supabase.from(tabla).insert(filas as never[])
        if (error) throw comoError(error)
      }
    }
    const { error } = await supabase.from('ajustes').upsert(s.ajustes, { onConflict: 'id' })
    if (error) throw comoError(error)
  },
}
